import React, { useState, useCallback, useMemo, useEffect } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'recompose'
import { connect } from 'react-redux'
import { waitFor, onMount } from 'lp-hoc'
import { Spinner } from 'lp-components'
import * as globalActions from 'partner-portal-actions'
import { SectionBlock } from 'components'
import { SchoolYearTabBar } from '../components'
import { ArtistPartnershipTableForm } from '../forms'
import * as apiActions from 'api-actions'
import * as actions from '../actions'
import { selectors } from '../reducer'
import { selectors as apiSelectors } from 'lp-redux-api'
import { selectors as globalPartnerSelectors } from 'partner-portal-reducer'
import { selectors as globalSelectors } from 'global-reducer'
import * as Types from 'types'
import { displaySubmitFailure, useCommunity, useUnmount } from 'utils'
import * as flashActions from 'redux-flash'
import { reset, destroy } from 'redux-form'
import { mapValues, first, groupBy, get } from 'lodash'
import syncProfileCompletion from '../sync-profile-completion'

const propTypes = {
  currentSchoolYear: Types.schoolYear.isRequired,
  destroyForms: PropTypes.func.isRequired,
  partner: Types.partner.isRequired,
  artistOptions: PropTypes.arrayOf(Types.searchSuggestion),
  setPartnerships: PropTypes.func.isRequired,
  setPartner: PropTypes.func.isRequired,
  updatePartner: PropTypes.func.isRequired,
  updateArtist: PropTypes.func.isRequired,
  activeSchoolYears: PropTypes.arrayOf(Types.schoolYear).isRequired,
  flashSuccessMessage: PropTypes.func.isRequired,
  resetForm: PropTypes.func.isRequired,
  fetchPartnershipQuestion: PropTypes.func.isRequired,
  isLoadingPartnershipQuestion: PropTypes.bool.isRequired,
}

const defaultProps = {
  updatedPartnerships: [],
}

function AssociatedTeachingArtist({
  partner,
  currentSchoolYear,
  destroyForms,
  artistOptions,
  activeSchoolYears,
  updatePartner,
  updateArtist,
  setPartner,
  flashSuccessMessage,
  resetForm,
  fetchPartnershipQuestion,
  isLoadingPartnershipQuestion,
}) {
  useEffect(() => {
    fetchPartnershipQuestion()
  }, [])

  const [selectedSchoolYear, setSelectedSchoolYear] =
    useState(currentSchoolYear)

  const schoolYearsById = useMemo(() => {
    return mapValues(groupBy(activeSchoolYears, 'id'), first)
  }, [activeSchoolYears])
  // Allow partners to edit the current and previous school year
  const editableSchoolYearIds = useMemo(() => {
    return activeSchoolYears.slice(0, 2).map(({ id }) => id)
  }, [activeSchoolYears])

  const { id: partnerId } = partner

  const partnerships = useMemo(() => {
    return partner.partnerships.filter(
      ({ teachingArtistId }) => !!teachingArtistId
    )
  }, [partner.partnerships])

  const updatePartnerForSchoolYear = useCallback(
    (params) => {
      return updatePartner(partnerId, selectedSchoolYear.id, params)
    },
    [partnerId, selectedSchoolYear.id]
  )

  const community = useCommunity()

  const handleSuccess = (updatedPartner) => {
    setPartner(updatedPartner)
    syncProfileCompletion({
      partner: updatedPartner,
      previousPartner: partner,
      community,
      updatePartnerBadge: updatePartnerForSchoolYear,
    })
    flashSuccessMessage('Partnership information updated!')
  }

  // Do not destroy forms as the user navigates between school year tabs, only on unmount of the parent component
  useUnmount(() => {
    const tabFormNames = activeSchoolYears.map((year) => {
      return `artist-partnership-table-${year.id}`
    })
    destroyForms(...tabFormNames)
  })

  const previousPartnerships = useMemo(() => {
    // Not worth calculating previous partnerships for years that can't copy them over
    if (!editableSchoolYearIds.includes(selectedSchoolYear.id)) return []
    return partnerships.filter(({ schoolYearId }) => {
      const existingPosition = get(
        schoolYearsById,
        `${schoolYearId}.position`,
        0
      )
      return existingPosition < selectedSchoolYear.position
    })
  }, [partnerships, schoolYearsById, selectedSchoolYear, editableSchoolYearIds])

  const isEditable = editableSchoolYearIds.includes(selectedSchoolYear.id)

  if (isLoadingPartnershipQuestion) return <Spinner />

  return (
    <React.Fragment>
      <div className="partnerships-year-select">
        <SchoolYearTabBar
          schoolYears={activeSchoolYears}
          schoolYear={selectedSchoolYear}
          onChange={setSelectedSchoolYear}
        />
        <SectionBlock>
          <ArtistPartnershipTableForm
            key={selectedSchoolYear.id} // important for triggering renders when switching tabs
            name={`artist-partnership-table-${selectedSchoolYear.id}`}
            initialValues={{
              partnerships: partnerships.filter(
                (p) => p.schoolYearId === selectedSchoolYear.id
              ),
            }}
            onSubmit={updatePartnerForSchoolYear}
            onSubmitSuccess={(partner) => {
              resetForm(`artist-partnership-table-${selectedSchoolYear.id}`) // removes partnerships with _destroy flag
              handleSuccess(partner)
            }}
            onSubmitFail={displaySubmitFailure}
            disabled={!isEditable}
            previousPartnerships={previousPartnerships}
            artistOptions={artistOptions}
            schoolYears={activeSchoolYears}
            visibleSchoolYearId={selectedSchoolYear.id}
            updateArtist={updateArtist}
          />
        </SectionBlock>
      </div>
    </React.Fragment>
  )
}

AssociatedTeachingArtist.propTypes = propTypes
AssociatedTeachingArtist.defaultProps = defaultProps

function mapStateToProps(state) {
  return {
    partner: globalPartnerSelectors.partner(state),
    partnershipQuestion: selectors.partnershipQuestion(state),
    currentPartnerId: globalPartnerSelectors.currentPartnerId(state),
    artistOptions: globalPartnerSelectors.artistOptions(state),
    currentSchoolYear: globalSelectors.currentSchoolYear(state),
    activeSchoolYears: globalPartnerSelectors.orderedActiveSchoolYears(state),
    isLoadingPartnershipQuestion: apiSelectors.isLoading(
      state,
      apiActions.fetchPartnershipQuestion
    ),
  }
}

const mapDispatchToProps = {
  destroyForms: destroy,
  setPartnerships: actions.setPartnerships,
  fetchArtistOptions: apiActions.fetchArtistOptions,
  fetchPartnershipQuestion: apiActions.fetchPartnershipQuestion,
  flashSuccessMessage: flashActions.flashSuccessMessage,
  resetForm: reset,
  setPartner: globalActions.setPartner,
  updatePartner: apiActions.updatePartner,
  updateArtist: apiActions.updateArtist,
}

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  onMount('fetchArtistOptions'),
  waitFor(['partner', 'artistOptions'])
)(AssociatedTeachingArtist)
